import { useState } from "react";
import {
  EDUCATION_LEVELS, COLLEGE_FIELDS, WORK_INDUSTRIES, OTHER_OPTION,
} from "./onboardingData";
import OnboardingScreen from "./OnboardingChrome";
import OnboardingSearchPicker from "./OnboardingSearchPicker";
import { contentArea, contentAreaFixed, optionBtn, heading, subline } from "./onboardingStyles";

// ─── CONDITIONAL FIELD FLOW ────────────────────────────────────────────────────
// Triggered right after the starter-categories screen, once per "Work" and/or
// "School" category the user picked (see OnboardingFlow.jsx, which owns the
// domain queue and re-mounts this component fresh for each domain). Not a
// separate section anymore — everyone who didn't pick Work or School never
// sees this at all.
//
// Work is a fixed two-screen flow (industry -> job). School branches by
// depth: Elementary/High School stop after one screen, College goes
// level -> field -> course — so navigation is a simple push/pop stack of
// screen names, same pattern the old standalone Section 3 used.
const APPROX_TOTAL = 3; // soft progress-bar denominator; College runs one screen deeper

const TRIGGER_LABEL = { work: "Work", school: "School" };

function OnboardingFieldFlow({ domain, direction, setDirection, anim, setAnim, onBack, answers, setAnswers, onDomainDone }) {
  const [stack, setStack] = useState(domain === "school" ? ["school-level"] : ["work-industry"]);
  const [otherText, setOtherText] = useState("");
  const screen = stack[stack.length - 1];
  const set = (patch) => setAnswers((a) => ({ ...a, ...patch }));

  // Moving between screens within this picker (industry -> job, level ->
  // field -> course) stays inside its own dots (slide) — entering/leaving
  // the picker entirely is decided by OnboardingFlow (onBack / onDomainDone)
  // before/after this component is mounted.
  const push = (next) => { setDirection("forward"); setAnim("slide"); setStack((s) => [...s, next]); };
  const back = stack.length > 1
    ? () => { setDirection("back"); setAnim("slide"); setStack((s) => s.slice(0, -1)); }
    : onBack;

  const done = (patch) => { if (patch) set(patch); setTimeout(onDomainDone, 220); };

  const screenProps = { direction, anim, progress: Math.min(stack.length, APPROX_TOTAL), total: APPROX_TOTAL, onBack: back };
  const subLabel = `You've added "${TRIGGER_LABEL[domain]}" to your list. Choose which ${domain === "school" ? "level" : "field"} you're in so Clarity can tailor habit suggestions to it.`;

  // ── work-industry: 18 industries, plain list ──
  if (screen === "work-industry") {
    return (
      <OnboardingScreen {...screenProps} stepKey="field-work-industry" onSkip={() => done()}>
        <div style={contentArea}>
          <div>
            <div style={heading}>What industry do you work in?</div>
            <div style={subline}>{subLabel}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {WORK_INDUSTRIES.map((ind) => (
              <button key={ind.id} style={optionBtn(answers.workIndustry === ind.id)} onClick={() => { set({ workIndustry: ind.id }); push("work-job"); }}>{ind.label}</button>
            ))}
          </div>
        </div>
      </OnboardingScreen>
    );
  }

  // ── work-job: search + A-Z, scoped to the chosen industry ──
  if (screen === "work-job") {
    const industry = WORK_INDUSTRIES.find((i) => i.id === answers.workIndustry);
    return (
      <OnboardingScreen {...screenProps} stepKey="field-work-job" onSkip={() => done()}>
        <div style={contentAreaFixed}>
          <div>
            <div style={heading}>What's your job title?</div>
            <div style={subline}>{industry?.label}</div>
          </div>
          <OnboardingSearchPicker
            items={industry?.jobs || []}
            selected={answers.workJob}
            placeholder="Search job titles…"
            onSelect={(val) => done({ workJob: val })}
            otherText={otherText}
            onOtherTextChange={setOtherText}
            onOtherSubmit={() => done({ workJob: OTHER_OPTION, workJobOther: otherText.trim() })}
          />
        </div>
      </OnboardingScreen>
    );
  }

  // ── school-level: Elementary / High School / College ──
  if (screen === "school-level") {
    const pick = (id) => {
      set({ educationLevel: id });
      setTimeout(() => {
        if (id === "college") push("college-field");
        else done();
      }, 220);
    };
    return (
      <OnboardingScreen {...screenProps} stepKey="field-school-level" onSkip={() => done()}>
        <div style={contentArea}>
          <div>
            <div style={heading}>What level are you in?</div>
            <div style={subline}>{subLabel}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {EDUCATION_LEVELS.map((o) => (
              <button key={o.id} style={optionBtn(answers.educationLevel === o.id)} onClick={() => pick(o.id)}>{o.label}</button>
            ))}
          </div>
        </div>
      </OnboardingScreen>
    );
  }

  // ── college-field: 16 fields, plain list ──
  if (screen === "college-field") {
    return (
      <OnboardingScreen {...screenProps} stepKey="field-college-field" onSkip={() => done()}>
        <div style={contentArea}>
          <div>
            <div style={heading}>What field are you studying?</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {COLLEGE_FIELDS.map((f) => (
              <button key={f.id} style={optionBtn(answers.collegeField === f.id)} onClick={() => { set({ collegeField: f.id }); push("college-course"); }}>{f.label}</button>
            ))}
          </div>
        </div>
      </OnboardingScreen>
    );
  }

  // ── college-course: search + A-Z, scoped to the chosen field ──
  const field = COLLEGE_FIELDS.find((f) => f.id === answers.collegeField);
  return (
    <OnboardingScreen {...screenProps} stepKey="field-college-course" onSkip={() => done()}>
      <div style={contentAreaFixed}>
        <div>
          <div style={heading}>What's your course?</div>
          <div style={subline}>{field?.label}</div>
        </div>
        <OnboardingSearchPicker
          items={field?.courses || []}
          selected={answers.collegeCourse}
          placeholder="Search courses…"
          onSelect={(val) => done({ collegeCourse: val })}
          otherText={otherText}
          onOtherTextChange={setOtherText}
          onOtherSubmit={() => done({ collegeCourse: OTHER_OPTION, collegeCourseOther: otherText.trim() })}
        />
      </div>
    </OnboardingScreen>
  );
}

export default OnboardingFieldFlow;
